import axios from "axios";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import {
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useRedoApplication } from "@/hooks/mutations/use-redo-application";
import { getApplicationCopy } from "@/lib/application-copy";
import { closeModal } from "@/store/modal.store";
import type { ExamCategory } from "@/types/test-execution";

interface RedoModalProps {
	applicationId: string;
	/** Obrigatória: o modal não tem como descobrir sozinho o que reinicia, e é
	 *  o momento em que o aluno apaga todas as respostas — precisa nomear o quê. */
	category: ExamCategory | number;
}

export function RedoModal({ applicationId, category }: RedoModalProps) {
	const { mutate, isPending } = useRedoApplication();
	const copy = getApplicationCopy(category);

	const handleRedo = () => {
		mutate(applicationId, {
			onSuccess: () => {
				closeModal();
				toast.success("Reinício autorizado");
				window.location.href = `/provas/${applicationId}`;
			},
			onError: (err) => {
				const message =
					axios.isAxiosError(err) && err.response?.data?.message
						? err.response.data.message
						: copy.redoError;
				toast.error(message);
			},
		});
	};

	return (
		<DialogContent className="max-w-md rounded-md p-8">
			<DialogHeader className="space-y-2">
				<DialogTitle className="text-2xl font-bold text-slate-900">
					{copy.redoTitle}
				</DialogTitle>
				<DialogDescription className="text-slate-500 text-base">
					{copy.redoDescription}
				</DialogDescription>
			</DialogHeader>
			<DialogFooter className="flex flex-col-reverse sm:flex-row gap-3 mt-8">
				<Button
					variant="ghost"
					onClick={closeModal}
					disabled={isPending}
					className="flex-1 h-14 rounded-md font-bold"
				>
					Cancelar
				</Button>
				<Button
					variant="destructive"
					onClick={handleRedo}
					disabled={isPending}
					className="flex-1 h-14 rounded-md font-bold"
				>
					{isPending ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							Reiniciando...
						</>
					) : (
						"Confirmar"
					)}
				</Button>
			</DialogFooter>
		</DialogContent>
	);
}
